import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import { User } from '../types';
import { API_BASE_URL } from '../utils/api';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | undefined>();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    user_type: 'engineer', // Keep for backward compatibility
    user_roles: [] as string[] // New field for multiple roles
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      const userData = response.data;
      if (Array.isArray(userData)) {
        setUsers(userData);
      } else {
        console.warn('Users API returned non-array data:', userData);
        setUsers([]);
      }
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
      setError('Failed to fetch users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate that at least one role is selected
    if (formData.user_roles.length === 0) {
      setError('Please select at least one role for the user');
      return;
    }
    
    try {
      console.log('Submitting user data:', formData);
      if (editingUser) {
        console.log('Updating user:', editingUser.id, formData);
        await axios.put(`${API_BASE_URL}/api/users/${editingUser.id}`, formData);
      } else {
        console.log('Creating new user:', formData);
        await axios.post(`${API_BASE_URL}/api/users`, formData);
      }
      setIsModalOpen(false);
      setEditingUser(undefined);
      resetForm();
      fetchUsers();
    } catch (err: any) {
      console.error('User operation error:', err);
      const errorMessage = err.response?.data?.error || (editingUser ? 'Failed to update user' : 'Failed to create user');
      setError(errorMessage);
    }
  };

  const handleDelete = async (userId: number) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/users/${userId}`);
        fetchUsers();
      } catch (err: any) {
        setError('Failed to delete user');
      }
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      username: user.username || '',
      email: user.email || '',
      password: '',
      first_name: user.first_name,
      last_name: user.last_name,
      user_type: user.user_type || 'engineer',
      user_roles: user.user_roles || (user.user_type ? [user.user_type] : ['engineer'])
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      user_type: 'engineer',
      user_roles: []
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRoleChange = (role: string, checked: boolean) => {
    setFormData(prev => {
      const newRoles = checked
        ? [...prev.user_roles, role]
        : prev.user_roles.filter(r => r !== role);
      
      // Update user_type for backward compatibility (use first role or 'engineer')
      const newUserType = newRoles.length > 0 ? newRoles[0] : 'engineer';
      
      return {
        ...prev,
        user_roles: newRoles,
        user_type: newUserType
      };
    });
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="container">
          <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1 className="page-title">User Management</h1>
          <button 
            onClick={() => setIsModalOpen(true)} 
            className="btn btn-primary"
          >
            Add New User
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="card">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e8f4f8' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Email</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Username</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ padding: '1rem' }}>
                    {user.first_name} {user.last_name}
                  </td>
                  <td style={{ padding: '1rem' }}>{user.email || 'N/A'}</td>
                  <td style={{ padding: '1rem' }}>{user.username || 'N/A'}</td>
                  <td style={{ padding: '1rem' }}>
                    {user.user_roles && user.user_roles.length > 0 ? (
                      <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                        {user.user_roles.map(role => (
                          <span key={role} className={`user-badge ${role === 'admin' ? 'admin' : 'engineer'}`}>
                            {role}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className={`user-badge ${user.user_type === 'admin' ? 'admin' : 'engineer'}`}>
                        {user.user_type || 'engineer'}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button 
                        onClick={() => handleEdit(user)} 
                        className="btn btn-primary"
                        style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDelete(user.id)} 
                        className="btn btn-danger"
                        style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {isModalOpen && (
          <div className="modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">{editingUser ? 'Edit User' : 'Add New User'}</h2>
                <button onClick={() => {
                  setIsModalOpen(false);
                  setEditingUser(undefined);
                  resetForm();
                }} className="close-btn">&times;</button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">First Name *</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Username *</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Password {editingUser ? '(leave blank to keep current)' : '*'}</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="form-input"
                    required={!editingUser}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">User Roles *</label>
                  <div style={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '4px', 
                    padding: '0.75rem',
                    backgroundColor: 'white'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.user_roles.includes('engineer')}
                          onChange={(e) => handleRoleChange('engineer', e.target.checked)}
                        />
                        <span>Engineer</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.user_roles.includes('admin')}
                          onChange={(e) => handleRoleChange('admin', e.target.checked)}
                        />
                        <span>Admin</span>
                      </label>
                    </div>
                    {formData.user_roles.length === 0 && (
                      <div style={{ 
                        color: '#dc3545', 
                        fontSize: '0.875rem', 
                        marginTop: '0.25rem' 
                      }}>
                        Please select at least one role
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => {
                    setIsModalOpen(false);
                    setEditingUser(undefined);
                    resetForm();
                  }} className="btn">
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingUser ? 'Update' : 'Create'} User
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;